import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { message } from "antd";
import { FaRegAddressCard } from "react-icons/fa6";
import { BiDetail, BiEdit, BiToggleLeft, BiToggleRight } from "react-icons/bi";

import {
  listarGruposDealer,
  alterarStatusGrupoDealer,
} from "../../services/http/cadastrosService";

const ITENS_POR_PAGINA = 10;

/**
 * Aba "Grupos Dealer" da tela de Cadastros. A RD 95133 não trazia esta tela no
 * documento; ela veio da resposta da Julia em 24/08 ("vamos precisar de uma tela
 * para criar, editar e desativar grupos"), já que o Grupo Dealer da concessionária
 * só aceita grupos previamente cadastrados e ativos.
 *
 * Novo/editar abrem em rota própria (GrupoDealerNovoEditar), no mesmo padrão da
 * concessionária e do usuário — não em modal.
 */
export default function ListaGruposDealer() {
  const history = useHistory();

  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("");
  const [buscaTimeout, setBuscaTimeout] = useState(null);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);

  const carregarLista = async (
    filtro = busca,
    status = statusFiltro,
    pagina = 1
  ) => {
    setCarregando(true);
    setErro(null);

    try {
      const resposta = await listarGruposDealer(
        pagina,
        ITENS_POR_PAGINA,
        filtro,
        status
      );

      if (resposta.fail) {
        setLista([]);
        setTotalItens(0);
        setTotalPaginas(0);
        setErro(resposta.errors?.message || "Erro ao carregar os grupos dealer");
        return;
      }

      setLista(resposta.data?.data ?? []);
      setTotalItens(resposta.data?.totalCount ?? 0);
      setTotalPaginas(resposta.data?.totalPages ?? 0);
    } catch (e) {
      console.error("Erro ao carregar grupos dealer:", e);
      setErro("Erro ao carregar os grupos dealer");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarLista("", "", 1);
  }, []);

  useEffect(() => {
    return () => {
      if (buscaTimeout) clearTimeout(buscaTimeout);
    };
  }, [buscaTimeout]);

  const aoBuscar = (evento) => {
    const valor = evento.target.value;
    setBusca(valor);
    setPaginaAtual(1);

    clearTimeout(buscaTimeout);
    setBuscaTimeout(setTimeout(() => carregarLista(valor, statusFiltro, 1), 500));
  };

  const aoFiltrarStatus = (evento) => {
    const valor = evento.target.value;
    setStatusFiltro(valor);
    setPaginaAtual(1);
    carregarLista(busca, valor, 1);
  };

  const aoTrocarPagina = (pagina) => {
    setPaginaAtual(pagina);
    carregarLista(busca, statusFiltro, pagina);
  };

  // Sem modal de confirmação: alterna direto e informa pela notificação.
  const aoAlterarStatus = async (grupo) => {
    const ativar = grupo.status !== "Ativo";

    const resposta = await alterarStatusGrupoDealer(grupo.id, ativar);

    if (resposta.fail) {
      message.error(
        resposta.errors?.message || "Não foi possível alterar o status"
      );
      return;
    }

    message.success(
      ativar ? `${grupo.nome} ativado` : `${grupo.nome} desativado`
    );
    carregarLista(busca, statusFiltro, paginaAtual);
  };

  return (
    <>
      <div className="cadastros-title-block">
        <h2>Grupos Dealer</h2>
        <p>Grupos usados no cadastro de concessionárias.</p>
      </div>

      <div
        className="filters-section p-3"
        style={{
          backgroundColor: "#E4E4E483",
          borderRadius: "10px",
          marginBottom: "20px",
        }}
      >
        <div className="row g-3 pb-4">
          <div className="col-12 col-md-6">
            <label
              htmlFor="buscaGrupo"
              className="form-label fw-semibold text-secondary"
            >
              Buscar Grupo
            </label>
            <input
              type="text"
              id="buscaGrupo"
              className="form-control"
              style={{
                height: "42px",
                fontSize: "14px",
                border: "1px solid #b1b1b1",
                borderRadius: "5px",
                color: "#495057",
              }}
              placeholder="Nome do grupo..."
              value={busca}
              onChange={aoBuscar}
            />
          </div>

          <div className="d-flex flex-column col-12 col-md-3">
            <label
              htmlFor="statusGrupoSelect"
              className="form-label fw-semibold text-secondary"
            >
              Status
            </label>
            <select
              id="statusGrupoSelect"
              className="form-select"
              value={statusFiltro}
              onChange={aoFiltrarStatus}
              style={{
                height: "42px",
                fontSize: "14px",
                border: "1px solid #b1b1b1",
                borderRadius: "5px",
                color: "#495057",
              }}
            >
              <option value="">Todos os status</option>
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
          </div>

          <div className="col-12 col-md-3 d-flex align-items-end">
            <button
              className="btn btn-primary w-100"
              onClick={() => history.push("/cadastros/grupos-dealer/novo")}
              style={{ height: "42px" }}
            >
              <FaRegAddressCard size={18} className="me-2" />
              Novo Grupo
            </button>
          </div>
        </div>
      </div>

      {carregando ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden"></span>
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="participants-table">
            <thead>
              <tr>
                <th>Grupo Dealer</th>
                <th>Concessionárias</th>
                <th>Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.length > 0 ? (
                lista.map((grupo) => (
                  <tr key={grupo.id}>
                    <td>{grupo.nome}</td>
                    <td>{grupo.qtdConcessionarias}</td>
                    <td>
                      <span
                        className={`status ${
                          grupo.status === "Ativo" ? "ativo" : "inativo"
                        }`}
                      >
                        {grupo.status}
                      </span>
                    </td>
                    <td>
                      <div className="cadastros-acoes">
                        <button
                          type="button"
                          title="Visualizar"
                          onClick={() =>
                            history.push(
                              `/cadastros/grupos-dealer/visualizar/${grupo.id}`
                            )
                          }
                        >
                          <BiDetail size={22} />
                        </button>
                        <button
                          type="button"
                          title="Editar"
                          onClick={() =>
                            history.push(
                              `/cadastros/grupos-dealer/editar/${grupo.id}`
                            )
                          }
                        >
                          <BiEdit size={22} />
                        </button>
                        <button
                          type="button"
                          title={grupo.status === "Ativo" ? "Desativar" : "Ativar"}
                          onClick={() => aoAlterarStatus(grupo)}
                        >
                          {grupo.status === "Ativo" ? (
                            <BiToggleRight size={34} />
                          ) : (
                            <BiToggleLeft size={34} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <div className="cadastros-empty">
                      {erro || "Nenhum grupo dealer cadastrado"}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPaginas > 0 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => aoTrocarPagina(paginaAtual - 1)}
            disabled={paginaAtual <= 1}
          >
            Anterior
          </button>

          <div className="pagination-numbers">
            {[...Array(totalPaginas)].map((_, indice) => (
              <button
                key={indice + 1}
                className={`pagination-number ${
                  paginaAtual === indice + 1 ? "active" : ""
                }`}
                onClick={() => aoTrocarPagina(indice + 1)}
              >
                {indice + 1}
              </button>
            ))}
          </div>

          <button
            className="pagination-button"
            onClick={() => aoTrocarPagina(paginaAtual + 1)}
            disabled={paginaAtual >= totalPaginas}
          >
            Próximo
          </button>
        </div>
      )}

      {totalItens > 0 && (
        <p className="text-muted" style={{ fontSize: "12px" }}>
          {totalItens} registro(s) encontrado(s)
        </p>
      )}

    </>
  );
}
